import { create } from "zustand";
import { useAppLoading } from "./useAppLoading";
import { LOG } from "../utils/logUtil";
import axios from "axios";

export interface IPartner {
  _id?: string;
  name?: string;
  image?: string;
  thumbnail?: string;
  en_description?: string;
  nl_description?: string;
  website?: string;
  active?: boolean;
  showInApp?: boolean;
  /**
   * Marks a partner as a parcel carrier (DPD, DHL). Carriers start hidden from
   * the app's partner catalogue (an admin can show one from its edit page) and
   * are always selectable in the parcel intake picker and in challenges.
   */
  isCourier?: boolean;
  order?: number;
  updatedAt?: Date;
  createdAt?: Date;
}

interface IStore {
  /** State */
  allPartners: IPartner[];
  /** Active carriers only, from the dedicated endpoint. */
  couriers: IPartner[];
  onePartner: IPartner | null;
  addedPartner: any | null;
  updatedPartner: any | null;
  deletedPartner: any | null;
  hasNextPage: boolean;
  /** Callbacks */
  getAllPartners: (page: number, limit: number) => void;
  getCouriers: () => void;
  getPartnersForSelect: () => void;
  getOnePartner: (id: string) => void;
  addPartner: (partner: IPartner) => void;
  updatePartner: (id: string, partner: IPartner) => void;
  deletePartner: (id: string) => void;
  reorderPartners: (items: { id: string; order: number }[]) => void;
  clearAddedPartner: () => void;
  clearUpdatedPartner: () => void;
  clearDeletedPartner: () => void;
}

/** Well above any realistic partner count; see getPartnersForSelect. */
const SELECT_PARTNER_LIMIT = 1000;

export const usePartners = create<IStore>()((set) => ({
  /** State */
  allPartners: [],
  couriers: [],
  onePartner: null,
  addedPartner: null,
  updatedPartner: null,
  deletedPartner: null,
  hasNextPage: false,
  /** Callbacks */
  getAllPartners: async (page, limit) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`partners?page=${page}&limit=${limit}`);
      if (response?.data) {
        set((state) => ({
          ...state,
          hasNextPage: response?.data?.length === limit,
          allPartners:
            page === 1
              ? response?.data
              : [...state.allPartners, ...response?.data],
        }));
      }
    } catch (error) {
      LOG.error("Error on fetching all Partners", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  // Its own endpoint rather than filtering allPartners: carriers are hidden
  // from the app catalogue, so the general list is not guaranteed to carry
  // them, and the challenge form must not offer a carrier that does not exist.
  getCouriers: async () => {
    try {
      const response = await axios.get("partners/couriers");
      if (response?.data) {
        set({ couriers: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching couriers", { error });
    }
  },
  /**
   * Every partner, for a dropdown.
   *
   * Screens with a partner <select> were each fetching their own page — 50
   * here, 100 there — and several skipped fetching entirely when the store
   * already held a list, whatever length that list happened to be. Both
   * silently truncate: GET /partners sorts by `order`, and a partner sorted
   * late (typically one hidden from the app, since reordering only renumbers
   * the visible ones) simply falls off the end. It showed up as IKEA missing
   * from the voucher screens in production but present in staging, which has
   * fewer partners.
   *
   * A select needs all of them or it is wrong, so this always refetches and
   * asks for a ceiling no partner list is going to reach.
   */
  getPartnersForSelect: async () => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(
        `partners?page=1&limit=${SELECT_PARTNER_LIMIT}`
      );
      if (response?.data) {
        set({ allPartners: response.data });
        // Hitting the ceiling means the list was cut off again — the same bug
        // this exists to fix, one order of magnitude later.
        if (response.data.length >= SELECT_PARTNER_LIMIT) {
          LOG.error(
            "Partner select list hit its limit and may be truncated",
            { limit: SELECT_PARTNER_LIMIT }
          );
        }
      }
    } catch (error) {
      LOG.error("Error on fetching partners for select", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getOnePartner: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`partners/${id}`);
      if (response?.data) {
        set({ onePartner: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching one Partner", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addPartner: async (partner) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("partners", partner);
      if (response?.data) {
        set({ addedPartner: response.data?._id });
      }
    } catch (error) {
      LOG.error("Error on adding Partner", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updatePartner: async (id, partner) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`partners/${id}`, partner);
      if (response?.data) {
        set({ updatedPartner: response.data });
      }
    } catch (error) {
      LOG.error("Error on updating Partner", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deletePartner: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.delete(`partners/${id}`);
      if (response?.data) {
        set((state) => ({
          deletedPartner: response.data,
          allPartners: state.allPartners.filter(
            (partner) => partner._id !== id
          ),
        }));
      }
    } catch (error) {
      LOG.error("Error on deleting Partner", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  reorderPartners: async (items) => {
    // Optimistic update
    set((state) => {
      const orderMap = new Map(items.map((i) => [i.id, i.order]));
      return {
        allPartners: state.allPartners.map((p) =>
          p._id && orderMap.has(p._id) ? { ...p, order: orderMap.get(p._id) } : p
        ),
      };
    });
    try {
      await axios.patch("partners/reorder", items);
    } catch (error) {
      LOG.error("Error on reordering partners", { error });
    }
  },
  clearAddedPartner: () => {
    set({ addedPartner: null });
  },
  clearUpdatedPartner: () => {
    set({ updatedPartner: null });
  },
  clearDeletedPartner: () => {
    set({ deletedPartner: null });
  },
}));
